import { useEffect, useState } from "react";
import { Plus, Trash2, ShieldCheck, ShoppingBag, Factory, RefreshCw, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { useAuth } from "@/auth/AuthProvider";
import { API } from "@/lib/api";
import { formatDateEU } from "@/lib/constants";

const ROLE_META = {
  super_admin: { color: "#C9A02A", icon: ShieldCheck, label: "Super Admin" },
  admin: { color: "#E85D04", icon: ShieldCheck, label: "Admin" },
  sales: { color: "#457B9D", icon: ShoppingBag, label: "Sales" },
  production: { color: "#7B6DCC", icon: Factory, label: "Production" },
};

const adminClient = axios.create({ baseURL: API, withCredentials: false });
adminClient.interceptors.request.use((config) => {
  const tok = typeof window !== "undefined" ? window.localStorage.getItem("garnacha_token") : null;
  if (tok) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${tok}`;
  }
  return config;
});
adminClient.interceptors.request.use((c) => {
  const tok = typeof window !== "undefined" ? window.localStorage.getItem("garnacha_token") : null;
  if (tok) {
    c.headers = c.headers || {};
    c.headers.Authorization = `Bearer ${tok}`;
  }
  return c;
});

export default function AuthorizedUsers() {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === "super_admin";
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("sales");
  const [loading, setLoading] = useState(false);
  // Welcome message editor state
  const [welcomeTarget, setWelcomeTarget] = useState(null);
  const [welcomeValue, setWelcomeValue] = useState("");
  const [savingWelcome, setSavingWelcome] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminClient.get("/admin/authorized-users");
      setUsers(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    const cleaned = email.trim().toLowerCase();
    if (!cleaned || !cleaned.includes("@")) return toast.error("Valid email required");
    try {
      await adminClient.post("/admin/authorized-users", { email: cleaned, role, active: true });
      toast.success(`Added ${cleaned}`);
      setEmail("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    }
  };

  const setRoleFor = async (u, newRole) => {
    try {
      await adminClient.put(`/admin/authorized-users/${u.id}`, {
        email: u.email, role: newRole, active: u.active,
      });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
  };

  const setActive = async (u, active) => {
    try {
      await adminClient.put(`/admin/authorized-users/${u.id}`, {
        email: u.email, role: u.role, active,
      });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
  };

  const del = async (u) => {
    if (!window.confirm(`Revoke access for ${u.email}?`)) return;
    try {
      await adminClient.delete(`/admin/authorized-users/${u.id}`);
      toast.success("Revoked");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  const openWelcome = (u) => {
    setWelcomeTarget(u);
    setWelcomeValue(u.welcome_message || "");
  };

  const saveWelcome = async () => {
    if (!welcomeTarget) return;
    setSavingWelcome(true);
    try {
      await adminClient.put(
        "/welcome/by-email",
        { welcome_message: welcomeValue },
        { params: { email: welcomeTarget.email } }
      );
      toast.success(`Welcome message updated for ${welcomeTarget.email}`);
      setWelcomeTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally { setSavingWelcome(false); }
  };


  return (
    <div data-testid="page-authorized-users">
      <PageHeader
        overline="Access Control"
        title="Authorized Users"
        subtitle="Only listed emails can sign in. Admins can edit this list; Sales & Production are read-only roles."
        actions={
          <Button variant="outline" onClick={load} disabled={loading} data-testid="reload-users">
            <RefreshCw size={16} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        }
      />

      <div className="card-soft p-4 mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-3 items-end">
          <div className="sm:col-span-3">
            <Label className="text-xs uppercase tracking-wider text-[#1F4D3A] font-semibold">Email</Label>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@garnacha.fi"
              data-testid="new-user-email"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-[#1F4D3A] font-semibold">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="super_admin">Super Admin</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="sales">Sales</SelectItem>
                <SelectItem value="production">Production</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={add} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="add-user-btn">
            <Plus size={16} className="mr-2" /> Authorize
          </Button>
        </div>
      </div>

      {users.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="No authorized users yet" description="Add an email above to grant access." />
      ) : (
        <div className="card-soft overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F1E8] text-[#1F4D3A] text-[10px] uppercase tracking-wider font-bold">
              <tr>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Active</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Created</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const RoleIcon = ROLE_META[u.role]?.icon || ShieldCheck;
                const color = ROLE_META[u.role]?.color || "#6C757D";
                return (
                  <tr key={u.id} className="border-t border-[#E5E5E5] hover:bg-[#F7F1E8]/30" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3 font-semibold text-[#222]">{u.email}</td>
                    <td className="px-4 py-3">
                      <Select value={u.role} onValueChange={(v) => setRoleFor(u, v)}>
                        <SelectTrigger
                          className="h-8 w-36 text-xs font-bold uppercase tracking-wider"
                          style={{ backgroundColor: `${color}14`, color, borderColor: `${color}33` }}
                          data-testid={`user-role-${u.id}`}
                        >
                          <RoleIcon size={12} className="mr-1" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="super_admin">Super Admin</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="sales">Sales</SelectItem>
                          <SelectItem value="production">Production</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <Switch checked={!!u.active} onCheckedChange={(v) => setActive(u, v)}
                        data-testid={`user-active-${u.id}`} />
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6C757D] hidden md:table-cell">{formatDateEU(u.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        {isSuperAdmin && (
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => openWelcome(u)}
                            title="Edit welcome message"
                            className="text-[#1F4D3A] hover:bg-[#1F4D3A]/10"
                            data-testid={`edit-welcome-${u.id}`}
                          >
                            <MessageSquare size={16} />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => del(u)}
                          className="text-[#E76F51] hover:bg-[#E76F51]/10"
                          data-testid={`delete-user-${u.id}`}>
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Welcome message editor (Super Admin only) */}
      <Dialog open={!!welcomeTarget} onOpenChange={(v) => { if (!v) setWelcomeTarget(null); }}>
        <DialogContent className="bg-white max-w-lg" data-testid="welcome-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Edit welcome message</DialogTitle>
          </DialogHeader>
          {welcomeTarget && (
            <div className="space-y-3">
              <div className="text-sm">
                <span className="brand-overline mr-2">User</span>
                <span className="font-semibold text-[#222]">{welcomeTarget.email}</span>
                {welcomeTarget.welcome_locked && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#C9A02A]/15 text-[#C9A02A] font-bold">Locked</span>
                )}
              </div>
              <Label className="text-xs uppercase tracking-wider text-[#1F4D3A] font-semibold">
                Welcome message (shown full-screen at login)
              </Label>
              <Textarea
                rows={5}
                value={welcomeValue}
                onChange={(e) => setWelcomeValue(e.target.value)}
                className="font-display text-lg"
                placeholder="e.g. El cielo es tu límite."
                data-testid="welcome-edit-textarea"
              />
              <div className="text-[10px] text-[#6C757D]">
                As Super Admin you can override locked messages.
              </div>
            </div>
          )}
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setWelcomeTarget(null)} data-testid="cancel-welcome-edit">Cancel</Button>
            <Button
              onClick={saveWelcome}
              disabled={savingWelcome}
              className="bg-[#E85D04] hover:bg-[#c94b03] text-white"
              data-testid="save-welcome-edit"
            >
              Save Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
